import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
} from 'react';
import { WIDTH_CONSTANT } from './trim-timeline';
import { TrimTime } from './video-editor';

interface Props {
	totalSeconds: number,
	elapsedSeconds: number,
	changeElapsedSeconds: Function,
	thumbTrackWidth: number,
	trimTimes: TrimTime,
	secondsInterval: number
}

export default function Playhead(props: Props) {
	const {
		totalSeconds,
		elapsedSeconds,
		changeElapsedSeconds,
		thumbTrackWidth,
		trimTimes,
		secondsInterval,
	} = props;

	const rangeRef = useRef<HTMLInputElement | null>(null);
	const playheadRef = useRef<HTMLDivElement | null>(null);

	const [rangeLeft, rangeWidth] = useMemo(() => {
		const nLeft = (trimTimes.start / secondsInterval) * WIDTH_CONSTANT;
		const nWidth = ((trimTimes.end - trimTimes.start) / secondsInterval) * WIDTH_CONSTANT;
		return [nLeft, nWidth];
	}, [trimTimes, secondsInterval]);

	const setPlayheadPosition = (value: number) => {
		const percSecondsAreOfMax = value / totalSeconds; // % of current time through whole track
		const secondsPercAsPx = thumbTrackWidth * percSecondsAreOfMax; // as px

		const leftSpacingStyles = -8; // padding margin

		const distanceFromLeft = secondsPercAsPx + leftSpacingStyles;

		if (playheadRef?.current) {
			playheadRef.current.style.left = `calc(${distanceFromLeft}px)`;
		}
	};

	useEffect(() => {
		if (rangeRef.current && playheadRef.current) {
			setPlayheadPosition(elapsedSeconds);
		}
	}, [rangeRef.current, playheadRef.current]);

	useEffect(() => {
		// move the playhead when current time updates or zoom is changed
		setPlayheadPosition(elapsedSeconds);
	}, [thumbTrackWidth, elapsedSeconds]);

	const handleChangeInput = useCallback(({ target }: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = Number(target.value);

		if (newValue !== elapsedSeconds) {
			changeElapsedSeconds(newValue);
			setPlayheadPosition(newValue);
		}
	}, [thumbTrackWidth, elapsedSeconds]);

	return (
		<div className='playhead-wrapper'>
			<div
				className="playhead-container"
				id="playhead"
				data-testid="playhead"
				ref={playheadRef}
			>
				<div className="playhead-top"/>
				<div className="playhead-line"/>
			</div>
			<input
				type="range"
				className="playhead-range"
				style={{
					maxWidth: `${rangeWidth}px`,
					minWidth: `${rangeWidth}px`,
					marginLeft: `${rangeLeft}px`,
				}}
				onChange={handleChangeInput}
				value={elapsedSeconds}
				min={trimTimes.start}
				max={trimTimes.end}
				step={0.2}
				ref={rangeRef}
			/>
		</div>
	);
}
